/**
 * Textarea with @-mention typeahead.
 *
 * UX:
 *   - User types `@` -> dropdown opens below the textarea, lists active team members
 *   - Typing more characters narrows the list (matches start of name/email)
 *   - Arrow up/down to navigate, Enter or Tab to insert
 *   - Picking a user inserts `@DisplayName ` into the text and emits the user_id
 *     in the parallel `mentions` set passed back via onMentionsChange
 *   - Esc dismisses the dropdown
 *
 * Storage convention: the body string contains literal `@DisplayName` substrings;
 * the corresponding user IDs live in a parallel array. This keeps the body
 * human-readable + lets the backend send notifications via the IDs.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchUsers, type TeamMember } from '@/lib/queries';
import { cn } from '@/lib/cn';

interface Props {
  value: string;
  onChange: (next: string) => void;
  mentions: string[];                          // user IDs currently mentioned
  onMentionsChange: (next: string[]) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
  /** offer "@everyone" at the top of the list, with this description */
  everyone?: string;
}

const MENTION_RE = /@(\w*)$/;
const EVERYONE = '__everyone__';   // captures the run of word-chars after @ at end of input

export default function MentionTextarea({
  value, onChange, mentions, onMentionsChange,
  placeholder, rows = 3, className, autoFocus, disabled,
  textareaRef: externalRef, everyone,
}: Props) {
  const internalRef = useRef<HTMLTextAreaElement | null>(null);
  const ref = externalRef ?? internalRef;

  const [activeQuery, setActiveQuery] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);

  // Lazy-load the team list only when the user actually opens the menu
  const users = useQuery({
    queryKey: ['team-members'],
    queryFn: fetchUsers,
    enabled: activeQuery !== null,
    staleTime: 5 * 60_000,
  });

  // Build the filtered candidate list. Skip clients (they shouldn't be @-able).
  const candidates: TeamMember[] = useMemo(() => {
    if (activeQuery === null) return [];
    const q = activeQuery.toLowerCase();
    const all = (users.data ?? []).filter(u => u.active && u.role !== 'client');
    // "@everyone" is a pseudo member: it inserts the word, the server expands it
    const every = everyone && 'everyone'.startsWith(q)
      ? [{ id: EVERYONE, display_name: 'everyone', email: '', role: everyone, designations: [], active: true } as unknown as TeamMember] : [];
    if (!q) return [...every, ...all.slice(0, 8)];
    return [...every, ...all
      .filter(u => u.display_name.toLowerCase().includes(q) || u.email.toLowerCase().startsWith(q))
      .slice(0, 8)];
  }, [users.data, activeQuery, everyone]);

  // Whenever value changes, detect if the caret is in an `@xxx` run and (re)open the menu
  function detectMention(text: string, caret: number) {
    const before = text.slice(0, caret);
    const m = before.match(MENTION_RE);
    if (m) {
      setActiveQuery(m[1]);
      setHighlight(0);
    } else {
      setActiveQuery(null);
    }
  }

  function onInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const next = e.target.value;
    onChange(next);
    detectMention(next, e.target.selectionStart ?? next.length);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (activeQuery === null || candidates.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => (h + 1) % candidates.length); return; }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => (h - 1 + candidates.length) % candidates.length); return; }
    if (e.key === 'Escape')    { e.preventDefault(); setActiveQuery(null); return; }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault();
      pick(candidates[highlight]);
    }
  }

  function pick(u: TeamMember) {
    const ta = ref.current;
    if (!ta) return;
    const caret = ta.selectionStart ?? value.length;
    const before = value.slice(0, caret);
    const m = before.match(MENTION_RE);
    if (!m) return;
    const start = caret - m[0].length;
    const insert = `@${u.display_name} `;
    const next = value.slice(0, start) + insert + value.slice(caret);
    onChange(next);
    if (u.id !== EVERYONE && !mentions.includes(u.id)) onMentionsChange([...mentions, u.id]);
    setActiveQuery(null);
    // Restore caret after the inserted mention
    setTimeout(() => {
      const newCaret = start + insert.length;
      ta.focus();
      ta.setSelectionRange(newCaret, newCaret);
    }, 0);
  }

  // If the user deletes a `@DisplayName` substring entirely, prune their id from mentions
  useEffect(() => {
    if (mentions.length === 0) return;
    // Hard to map id -> name without a lookup; skip pruning here. The backend will
    // still send the notification because the id is in the array - which is fine
    // (over-notify is much less bad than under-notify). The next save replaces.
  }, [value, mentions]);

  return (
    <div className="relative">
      <textarea
        // Cast: React's textarea ref attribute is typed as LegacyRef<HTMLTextAreaElement>
        // (no null in the generic). Our internal/external refs allow null, which is
        // semantically fine - textareas always assign a non-null current at mount time.
        ref={ref as React.RefObject<HTMLTextAreaElement>}
        value={value}
        onChange={onInput}
        onKeyDown={onKeyDown}
        onBlur={() => setTimeout(() => setActiveQuery(null), 150)}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        autoFocus={autoFocus}
        className={cn(
          'w-full text-sm bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-muted-strong placeholder:text-muted/50 focus:outline-none focus:border-accent-400 disabled:opacity-50',
          className,
        )}
      />

      {/* Typeahead popover - anchored to the textarea bottom-left */}
      {activeQuery !== null && (
        <div className="absolute left-0 right-0 top-full mt-1 z-30 rounded-lg bg-slate-900 ring-1 ring-slate-700 shadow-xl overflow-hidden max-h-64 overflow-y-auto">
          {users.isLoading && <div className="p-3 text-xs text-muted text-center">Loading team...</div>}
          {!users.isLoading && candidates.length === 0 && (
            <div className="p-3 text-xs text-muted text-center">
              No teammates match {activeQuery ? `"${activeQuery}"` : ''}
            </div>
          )}
          {candidates.map((u, i) => (
            <button
              key={u.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); pick(u); }}
              onMouseEnter={() => setHighlight(i)}
              className={cn(
                'w-full text-left px-3 py-2 flex items-center gap-2.5 text-xs transition',
                i === highlight ? 'bg-accent-600/20' : 'hover:bg-slate-800',
              )}
            >
              {u.photo_url ? (
                <img src={u.photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-slate-700 flex items-center justify-center text-[10px] text-muted-strong font-medium">
                  {u.display_name.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-muted-strong truncate">{u.display_name}</div>
                <div className="text-[10px] text-muted truncate">
                  {u.id === EVERYONE ? u.role : <>{u.role}{u.designations.length > 0 && ` · ${u.designations.slice(0, 2).join(', ')}`}</>}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Render a comment body with @mentions highlighted as accent-colored pills. */
export function MentionBody({ text, className }: { text: string; className?: string }) {
  // Split on `@Word(s)` runs - includes spaces in display names? Display names
  // can have spaces (e.g. "Lara Khoury"). We can't know where the mention ends
  // without the user IDs. For MVP, only highlight single-word mentions; the
  // first-name match covers the common case.
  const parts = text.split(/(@[\w-]+)/g);
  return (
    <span className={cn('whitespace-pre-wrap', className)}>
      {parts.map((p, i) => {
        if (p.startsWith('@') && p.length > 1) {
          return (
            <span key={i} className="text-accent-300 bg-accent-600/15 rounded px-1 py-0.5 font-medium">
              {p}
            </span>
          );
        }
        return <span key={i}>{p}</span>;
      })}
    </span>
  );
}
